import React, { useEffect, useState, useRef } from "react";
import { MapPin, Loader2, Check } from "lucide-react";

/**
 * Autocomplete indirizzo via OpenStreetMap Nominatim (gratuito, no API key).
 *
 * Props:
 *  - value: string (indirizzo testuale)
 *  - onSelect: ({ address, city, lat, lng }) => void
 *      Chiamato quando l'utente sceglie un risultato dal dropdown.
 *  - onChange: (text) => void
 *      Chiamato ad ogni keystroke (per mantenere lo stato controllato).
 *  - locked: bool — true se l'indirizzo è stato confermato (lat/lng presenti).
 *      Mostra un check verde discreto.
 *  - placeholder, testid
 */
export default function AddressAutocomplete({
    value,
    onSelect,
    onChange,
    locked = false,
    placeholder = "Inizia a scrivere l'indirizzo (es. Via Roma 12, Milano)",
    testid = "address-autocomplete",
}) {
    const [query, setQuery] = useState(value || "");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [activeIdx, setActiveIdx] = useState(-1);
    const debRef = useRef(null);
    const wrapRef = useRef(null);
    const lastConfirmed = useRef(value || "");

    // Sync external value (e.g., when restaurant data loads after mount)
    useEffect(() => {
        setQuery(value || "");
        lastConfirmed.current = value || "";
    }, [value]);

    // Debounced fetch
    useEffect(() => {
        if (debRef.current) clearTimeout(debRef.current);
        const q = query.trim();
        // Don't search again if user just selected a result
        if (q.length < 4 || q === lastConfirmed.current) {
            setResults([]);
            return;
        }
        debRef.current = setTimeout(async () => {
            setLoading(true);
            try {
                const url = new URL("https://nominatim.openstreetmap.org/search");
                url.searchParams.set("format", "json");
                url.searchParams.set("addressdetails", "1");
                url.searchParams.set("limit", "6");
                url.searchParams.set("countrycodes", "it");
                url.searchParams.set("accept-language", "it");
                url.searchParams.set("q", q);
                const res = await fetch(url.toString(), {
                    headers: { "Accept": "application/json" },
                });
                if (!res.ok) throw new Error("nominatim error");
                const data = await res.json();
                setResults(data || []);
                setOpen(true);
                setActiveIdx(-1);
            } catch {
                setResults([]);
            } finally {
                setLoading(false);
            }
        }, 450);
        return () => clearTimeout(debRef.current);
    }, [query]);

    // Click-outside to close
    useEffect(() => {
        const onClick = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    const pick = (r) => {
        const a = r.address || {};
        const city = a.city || a.town || a.village || a.municipality || a.county || "";
        const street = [a.road, a.house_number].filter(Boolean).join(" ");
        const fullParts = [
            street,
            a.postcode,
            city,
            a.state,
        ].filter(Boolean);
        const formatted = fullParts.join(", ") || r.display_name;
        const lat = parseFloat(r.lat);
        const lng = parseFloat(r.lon);
        setQuery(formatted);
        lastConfirmed.current = formatted;
        setOpen(false);
        setResults([]);
        onSelect?.({ address: formatted, city, lat, lng });
    };

    const onKeyDown = (e) => {
        if (!open || results.length === 0) return;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveIdx((i) => Math.min(i + 1, results.length - 1));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIdx((i) => Math.max(i - 1, 0));
        } else if (e.key === "Enter" && activeIdx >= 0) {
            e.preventDefault();
            pick(results[activeIdx]);
        } else if (e.key === "Escape") {
            setOpen(false);
        }
    };

    return (
        <div ref={wrapRef} className="relative" data-testid={testid}>
            <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gold/70" size={14} strokeWidth={1.5} />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => {
                        const v = e.target.value;
                        setQuery(v);
                        onChange?.(v);
                        // If user edits the confirmed address, mark it as not yet confirmed
                        if (v !== lastConfirmed.current) lastConfirmed.current = "";
                    }}
                    onFocus={() => results.length && setOpen(true)}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    data-testid={`${testid}-input`}
                    className="w-full pl-9 pr-9 py-2.5 bg-transparent border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none"
                    autoComplete="off"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {loading ? (
                        <Loader2 size={14} className="text-gold/70 animate-spin" />
                    ) : locked ? (
                        <Check size={14} className="text-emerald-400" data-testid={`${testid}-confirmed`} />
                    ) : null}
                </div>
            </div>

            {open && results.length > 0 && (
                <ul
                    className="absolute z-30 mt-1 w-full bg-ink-surface border border-white/15 rounded-sm shadow-lg max-h-64 overflow-y-auto"
                    data-testid={`${testid}-results`}
                >
                    {results.map((r, idx) => {
                        const a = r.address || {};
                        const city = a.city || a.town || a.village || a.municipality || a.county || "";
                        const region = a.state || "";
                        const street = [a.road, a.house_number].filter(Boolean).join(" ") || r.display_name.split(",")[0];
                        return (
                            <li key={r.place_id}>
                                <button
                                    type="button"
                                    onClick={() => pick(r)}
                                    onMouseEnter={() => setActiveIdx(idx)}
                                    data-testid={`${testid}-result-${idx}`}
                                    className={`w-full text-left px-3 py-2.5 border-b border-white/5 last:border-b-0 transition-colors ${
                                        idx === activeIdx ? "bg-gold/10" : "hover:bg-white/5"
                                    }`}
                                >
                                    <div className="text-sm text-white truncate">{street}</div>
                                    <div className="text-[11px] text-gray-400 truncate">
                                        {[city, region].filter(Boolean).join(" · ")}
                                    </div>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}
